import { StatCard } from "./StatCard";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertTriangle, ClipboardCheck, HeadphonesIcon, Shield,
  Users, BookOpen, Activity, Bell,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export function DeputyPrincipalDashboard() {
  const { data: pendingApps = 0 } = useQuery({
    queryKey: ["deputy-pending-apps"],
    queryFn: async () => {
      const { data } = await supabase.from("applications").select("status").eq("status", "pending");
      return data?.length ?? 0;
    },
  });

  const { data: pendingResults = 0 } = useQuery({
    queryKey: ["deputy-pending-results"],
    queryFn: async () => {
      const { data } = await supabase.from("exam_results").select("status").eq("status", "pending");
      return data?.length ?? 0;
    },
  });

  const { data: pendingRegistrations = 0 } = useQuery({
    queryKey: ["deputy-pending-regs"],
    queryFn: async () => {
      const { data } = await supabase.from("registrations").select("status").eq("status", "registered");
      return data?.length ?? 0;
    },
  });

  const { data: openServices = 0 } = useQuery({
    queryKey: ["deputy-open-services"],
    queryFn: async () => {
      const { data } = await supabase.from("service_requests").select("status").in("status", ["open", "escalated"]);
      return data?.length ?? 0;
    },
  });

  const { data: openDiscipline = 0 } = useQuery({
    queryKey: ["deputy-open-discipline"],
    queryFn: async () => {
      const { data } = await supabase.from("discipline_cases").select("status").in("status", ["open", "investigation"]);
      return data?.length ?? 0;
    },
  });

  const { data: overdueInvoices = 0 } = useQuery({
    queryKey: ["deputy-overdue-invoices"],
    queryFn: async () => {
      const { data } = await supabase.from("invoices").select("due_date, status").eq("status", "unpaid");
      return data?.filter(i => i.due_date && new Date(i.due_date) < new Date()).length ?? 0;
    },
  });

  const { data: recentAlerts = [] } = useQuery({
    queryKey: ["deputy-alerts"],
    queryFn: async () => {
      const alerts: { label: string; count: number; severity: "warning" | "danger" | "info" }[] = [];
      if (pendingApps > 0) alerts.push({ label: "Pending applications need review", count: pendingApps, severity: "warning" });
      if (pendingResults > 0) alerts.push({ label: "Exam results awaiting approval", count: pendingResults, severity: "warning" });
      if (overdueInvoices > 0) alerts.push({ label: "Overdue invoices", count: overdueInvoices, severity: "danger" });
      if (openDiscipline > 0) alerts.push({ label: "Open discipline cases", count: openDiscipline, severity: "danger" });
      if (openServices > 0) alerts.push({ label: "Unresolved service requests", count: openServices, severity: "info" });
      return alerts;
    },
    enabled: pendingApps !== undefined,
  });

  const totalPendingApprovals = pendingApps + pendingResults;

  return (
    <>
      <div className="grid gap-2 sm:gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Pending Approvals" value={totalPendingApprovals} icon={ClipboardCheck} description="Applications & results" />
        <StatCard title="Pending Registrations" value={pendingRegistrations} icon={BookOpen} description="Awaiting processing" />
        <StatCard title="Unresolved Issues" value={openServices + openDiscipline} icon={AlertTriangle} description="Service + discipline" trend={openServices + openDiscipline > 5 ? "down" : "neutral"} />
        <StatCard title="Overdue Invoices" value={overdueInvoices} icon={Activity} description="Follow-up needed" trend={overdueInvoices > 0 ? "down" : "neutral"} />
      </div>

      <div className="grid gap-2 sm:gap-3 grid-cols-1 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><Bell className="h-5 w-5 text-primary" />Daily Operational Alerts</CardTitle>
            <CardDescription>Items requiring attention today</CardDescription>
          </CardHeader>
          <CardContent>
            {recentAlerts.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No alerts — all clear!</p>
            ) : (
              <div className="space-y-3">
                {recentAlerts.map((alert, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div className="flex items-center gap-3">
                      <div className={`h-2 w-2 rounded-full ${alert.severity === "danger" ? "bg-destructive" : alert.severity === "warning" ? "bg-accent-orange" : "bg-primary"}`} />
                      <span className="text-sm">{alert.label}</span>
                    </div>
                    <span className="text-sm font-semibold">{alert.count}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Department Status</CardTitle>
            <CardDescription>Cross-department follow-up tasks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { dept: "Admission", issue: `${pendingApps} pending applications`, icon: Users },
                { dept: "Examination", issue: `${pendingResults} unapproved results`, icon: ClipboardCheck },
                { dept: "Student Services", issue: `${openServices} unresolved requests`, icon: HeadphonesIcon },
                { dept: "Discipline", issue: `${openDiscipline} open cases`, icon: Shield },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 py-2 border-b last:border-0">
                  <item.icon className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{item.dept}</p>
                    <p className="text-xs text-muted-foreground">{item.issue}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
